import type { LabelMetrics } from '../labelPrintTypes';
import type { LabelStyleMetricOptions, LabelStyleMetrics } from './styleTypes';

export const buildStyleMetrics = (m: LabelMetrics, opts: LabelStyleMetricOptions): LabelStyleMetrics => {
    const {
        horizontal,
        isMaterialMode,
        applyMaterialZoom,
        lineHeightMultiplier = 1,
        is6LabelLayout = false,
        leftMarginMm = 0,
        isForPrint = false,
    } = opts;

    const isSplotMode = !isMaterialMode;
    const isSplotHorizontal = horizontal && isSplotMode;
    const materialTextScaleFactor = isMaterialMode && applyMaterialZoom ? 1.15 : 1;
    const isSplotPrint = isSplotMode && applyMaterialZoom;

    const splotBasePaddingMm = is6LabelLayout
        ? 0.6
        : (isSplotHorizontal ? 0.9 : Math.max(1.35, Math.min(m.widthMm, m.heightMm) * 0.09));
    const splotPrintLeftPaddingMm = is6LabelLayout
        ? leftMarginMm
        : (isSplotPrint ? (isSplotHorizontal ? 6 : 3) : 0);
    const paddingMm = isSplotMode ? splotBasePaddingMm : 2;
    const containerPadding = isSplotMode
        ? `${splotBasePaddingMm}mm ${splotBasePaddingMm}mm ${splotBasePaddingMm}mm ${splotBasePaddingMm + splotPrintLeftPaddingMm}mm`
        : `${paddingMm}mm`;

    const w = m.widthMm;
    const h = m.heightMm;
    const gridRows = horizontal ? (isMaterialMode ? 'auto 1fr 1fr auto' : '1fr') : 'auto 1fr';
    // Material rows: height is grid-track-controlled, so no per-row max-height constraint.
    const rowMaxHeight = isMaterialMode ? h : ((isForPrint && horizontal) ? h : (horizontal ? h : h / 2));
    // Material rows own their padding via class overrides; base padding is 0 for material.
    const rowHorizontalPaddingMm = isSplotMode ? (isSplotHorizontal ? 1.25 : 1.5) : 0;
    const rowGapMm = isSplotMode ? (isSplotHorizontal ? 1.8 : 2.0) : 0;
    const splotTextInsetXmm = isSplotHorizontal ? 0.7 : 0.5;
    const splotTextInsetYmm = isSplotHorizontal ? 0.35 : 0.25;

    const splotIdFontMm = isSplotHorizontal
        ? Math.max(m.titleFontMm * 2, h * 0.115)
        : m.titleFontMm;
    const splotIdLetterSpacingMm = isSplotHorizontal ? 0.18 : 0.3;
    const splotIdLineHeight = (isMaterialMode ? (horizontal ? 1.65 : 1.5) : 2) * lineHeightMultiplier;

    const materialIdFontMm = isMaterialMode
        // Title font derives from label HEIGHT, so on near-square labels it can exceed the
        // available WIDTH and get ellipsed. Cap it to the width a ~13-char MatID needs.
        ? Math.min(
            m.titleFontMm * 1.55 * materialTextScaleFactor,
            Math.max(0, m.widthMm - 2 * paddingMm - 2) / (13 * 0.6),
        )
        : m.titleFontMm;
    const materialIdLineHeight = (isMaterialMode ? (horizontal ? 1.2 : 1.3) : 1.2) * lineHeightMultiplier;

    const containerIdFontMm = m.footerFontMm;  // Use footer size for all modes
    const containerIdLineHeight = (isMaterialMode ? (horizontal ? 1.2 : 1.3) : 1.2) * lineHeightMultiplier;
    // Admin Code / Trial ID share the bottom row with a QR, so cap the font to the width
    // that actually remains beside it. Ratio allows ~18 uppercase chars (~0.72em each).
    const adminCodeTextWidthMm = Math.max(0, m.widthMm - 2 * paddingMm - 2 - m.qrMm - 1.5);
    const adminCodeFontMm = isMaterialMode
        ? Math.min(m.originFontMm * 1.35 * materialTextScaleFactor, adminCodeTextWidthMm / (18 * 0.72))
        : m.originFontMm * 1.35;
    const adminCodeLineHeight = (isMaterialMode ? (horizontal ? 1.2 : 1.2) : 1.3) * lineHeightMultiplier;
    const originLineHeight = (isMaterialMode ? (horizontal ? 1.2 : 1.2) : 1.3) * lineHeightMultiplier;
    const footerLineHeight = (isMaterialMode ? (horizontal ? 1.15 : 1.15) : 1.25) * lineHeightMultiplier;
    const footerFontMmScaled = isMaterialMode ? m.footerFontMm * materialTextScaleFactor : m.footerFontMm;
    const originFontMmScaled = isMaterialMode ? m.originFontMm * materialTextScaleFactor : m.originFontMm;

    return {
        isSplotMode,
        isSplotHorizontal,
        w,
        h,
        gridRows,
        rowMaxHeight,
        rowHorizontalPaddingMm,
        rowGapMm,
        splotTextInsetXmm,
        splotTextInsetYmm,
        containerPadding,
        effectiveOuterLeftMarginMm: is6LabelLayout ? 0 : leftMarginMm,
        splotIdFontMm,
        splotIdLetterSpacingMm,
        splotIdLineHeight,
        materialIdFontMm,
        materialIdLineHeight,
        containerIdFontMm,
        containerIdLineHeight,
        adminCodeFontMm,
        adminCodeLineHeight,
        originLineHeight,
        footerLineHeight,
        footerFontMmScaled,
        originFontMmScaled,
    };
};

export const buildSharedElementCss = (m: LabelStyleMetrics, horizontal: boolean): string => `
        .label-row {
            display: flex; align-items: center;
            overflow: hidden; min-height: 0;
            max-width: ${m.w}mm; ${m.isSplotMode ? `max-height: ${m.rowMaxHeight}mm;` : ''}
            padding: 0 ${m.rowHorizontalPaddingMm}mm; gap: ${m.rowGapMm}mm;
        }
        .label-row-vertical {
            display: flex; flex-direction: column;
            align-items: center; justify-content: center;
            overflow: hidden; min-height: 0;
            max-width: ${m.w}mm; max-height: ${m.h}mm;
            padding: 1mm; gap: 2.5mm;
        }
        .qr-cell {
            flex-shrink: 0;
            display: flex; flex-direction: ${horizontal ? 'row' : 'column'};
            justify-content: center; align-items: center;
            overflow: hidden;
        }
        .qr-cell div, .qr-cell span { display: block; }
        .qr-cell svg { width: 100%; height: 100%; }
        .text-cell {
            display: flex; flex-direction: column;
            justify-content: center;
            overflow: hidden; min-width: 0; flex: 1;
            max-width: 100%;
            ${m.isSplotMode ? `padding: ${m.splotTextInsetYmm}mm ${m.splotTextInsetXmm}mm;` : ''}
        }
        .text-cell-center {
            display: flex; flex-direction: column;
            align-items: center; justify-content: center;
            overflow: hidden; min-width: 0;
            max-width: 100%;
        }
        .footer-row { display: flex; gap: 2mm; }
        .material-banner-row {
            align-items: center;
            padding: 0 ${m.isSplotMode ? m.splotTextInsetXmm : 1}mm;
        }
        .material-qr-row {
            align-items: center;
            gap: 1.5mm;
            padding: 0 ${m.isSplotMode ? m.splotTextInsetXmm : 1}mm;
        }
        .material-bottom-row {
            align-items: flex-start;
            gap: 1.5mm;
            padding: 0 ${m.isSplotMode ? m.splotTextInsetXmm : 1}mm;
        }
        .material-bottom-row .text-cell {
            justify-content: flex-end;
            padding-top: 0.5mm;
        }
        .material-bottom-row .footer-text:first-child {
            margin-top: 0.3mm;
        }
        .material-footer-row {
            align-items: center;
            justify-content: flex-end;
            padding: 0 ${m.isSplotMode ? m.splotTextInsetXmm : 1}mm;
        }
        .material-qr-with-id {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            gap: 0.5mm;
            flex-shrink: 0;
        }
        .material-id-banner {
            display: block;
            width: 100%;
            font-weight: 700;
            font-size: ${m.materialIdFontMm}mm;
            line-height: ${m.materialIdLineHeight};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .splot-id {
            font-weight: 700; font-size: ${m.splotIdFontMm}mm;
            letter-spacing: ${m.splotIdLetterSpacingMm}mm; white-space: nowrap;
            line-height: ${m.splotIdLineHeight};
            overflow: hidden; text-overflow: ellipsis;
            max-width: 100%;
            ${m.isSplotMode ? `margin: ${m.splotTextInsetYmm}mm 0;` : ''}
        }
        .origin {
            font-weight: 700; font-size: ${m.originFontMmScaled}mm;
            line-height: ${m.originLineHeight}; white-space: nowrap;
            overflow: hidden; text-overflow: ellipsis;
            max-width: 100%;
        }
        .footer-text {
            font-weight: 600; font-size: ${m.footerFontMmScaled}mm;
            line-height: ${m.footerLineHeight};
            white-space: nowrap;
            overflow: hidden; text-overflow: ellipsis;
            max-width: 100%;
        }
        .container-id {
            font-weight: 700; font-size: ${m.containerIdFontMm}mm;
            line-height: ${m.containerIdLineHeight};
            white-space: nowrap;
            overflow: hidden; text-overflow: ellipsis;
            max-width: 100%;
        }
        .admin-code {
            font-weight: 700; font-size: ${m.adminCodeFontMm}mm;
            line-height: ${m.adminCodeLineHeight};
            white-space: nowrap;
            overflow: hidden; text-overflow: ellipsis;
            max-width: 100%;
        }
`;